import {openDialog, closeCurrentDialog, DialogsContext} from 'platform/components';
import {Show, Text} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useContext} from 'react';

import {always, filter, isNil, isNotNil, sortBy} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  ServiceCaseIntegration,
  ServiceCaseIntegrationActionKey,
  useGetServiceCaseIntegrationsQuery,
  usePostServiceCaseIntegrationsXentryOrderSynchronizationApiMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';
import {useServiceCaseDetailActions} from '@dms/shared-aftersales';

import {TestIdProps, noop, suffixTestId, Nullish} from 'shared/web';

import {EvaClaimDialog} from '../EvaClaimDialog/EvaClaimDialog';
import {IntegrationPopper} from '../IntegrationPopper/IntegrationPopper';
import {SyncXentry} from '../SyncXentry/SyncXentry';
import {ClaraVehicleInfoView} from './components/ClaraVehicleInfoView';
import {IntegrationItem} from './components/IntegrationItem';
import {SERVICE_CASE_INTEGRATION_ACTION_PREFIX} from './constants/actionKeys';

interface IntegrationsDropdownProps extends TestIdProps {
  serviceCaseId: string;
  isExternalActionPending?: boolean;
}

export function IntegrationsDropdown(props: IntegrationsDropdownProps) {
  const {executeAction, isActionPending} = useServiceCaseDetailActions();

  const {data, isError, isLoading, refetch} = useGetServiceCaseIntegrationsQuery(
    {serviceCaseId: props.serviceCaseId},
    {skip: isNilOrEmpty(props.serviceCaseId)}
  );

  const [postXentrySync] = usePostServiceCaseIntegrationsXentryOrderSynchronizationApiMutation();

  const integrations = sortBy(
    (integration) => integration?.position ?? 0,
    data?.caseIntegration ?? []
  );

  const activeIntegrations: ServiceCaseIntegration[] = filter(
    (integration) =>
      isNotNil(integration?.integration?.createdAt) ||
      isNotNil(integration?.integration?.updatedAt),
    integrations
  );
  const availableIntegrations: ServiceCaseIntegration[] = filter(
    (integration) =>
      isNil(integration?.integration?.createdAt) && isNil(integration?.integration?.updatedAt),
    integrations
  );

  const hasActiveIntegrations = isNotNilOrEmpty(activeIntegrations);
  const hasAvailableIntegrations = isNotNilOrEmpty(availableIntegrations);

  const {isAnyDialogOpen} = useContext(DialogsContext);

  const onActionClick = (actionKey?: ServiceCaseIntegrationActionKey | Nullish) => {
    if (isNil(actionKey)) {
      return;
    }

    const integrationActionKey = `${SERVICE_CASE_INTEGRATION_ACTION_PREFIX}${actionKey}`;

    match(actionKey)
      .with('clara-vehicle-info-view', () => {
        openDialog(
          <ClaraVehicleInfoView
            serviceCaseId={props.serviceCaseId}
            data-testid={suffixTestId('claraVehicleInfoView', props)}
          />,
          {title: i18n.t('entity.integration.labels.claraVehicleInfo'), size: 'large'}
        );
      })
      .with('xentry-job-sync', () =>
        executeAction(integrationActionKey, () =>
          postXentrySync({
            serviceCaseId: props.serviceCaseId,
          })
            .unwrap()
            .then((data) => {
              if (!data) {
                refetch();
                return;
              }

              openDialog(
                <SyncXentry
                  serviceCaseId={props.serviceCaseId}
                  groups={data.groups ?? []}
                  data-testid={suffixTestId('syncXentry', props)}
                />,
                {
                  title: i18n.t('entity.integration.labels.xentrySync'),
                  id: 'selectXentryDialog',
                  size: 'large',
                }
              );
            })
            .catch(handleApiError)
        )
      )
      .with('eva-claim-create', () => {
        openDialog(
          <EvaClaimDialog
            serviceCaseId={props.serviceCaseId}
            onClose={closeCurrentDialog}
            data-testid={suffixTestId('evaClaimDialog', props)}
          />,
          {
            title: i18n.t('entity.integration.labels.mercedesEvaClaim'),
            size: 'large',
          }
        );
      })
      .otherwise(always(noop));
  };

  const isAnyActionAllowed = integrations.some(
    (integration) => integration?.action?.some((action) => action?.style !== 'disabled') ?? false
  );

  return (
    <IntegrationPopper
      buttonTitle={i18n.t('entity.orderIntegration.labels.integration')}
      buttonVariant="secondary"
      isButtonDisabled={!isAnyActionAllowed || props.isExternalActionPending}
      isLoading={isLoading}
      isError={isError}
      onOpen={refetch}
      isOutsideClickIgnored={isAnyDialogOpen}
      data-testid={props['data-testid']}
    >
      <Show when={hasActiveIntegrations}>
        <Text color="secondary">{i18n.t('entity.integration.labels.activeIntegrations')}</Text>
        {activeIntegrations.map((integration, index) => (
          <IntegrationItem
            key={`active-${integration?.integration?.featureCode}-${index}`}
            integration={integration}
            isActionPending={isActionPending}
            onActionClick={onActionClick}
            isExternalActionPending={props.isExternalActionPending}
            data-testid={props['data-testid']}
          />
        ))}
      </Show>
      <Show when={hasAvailableIntegrations}>
        <Text color="secondary">{i18n.t('entity.integration.labels.availableIntegrations')}</Text>
        {availableIntegrations.map((integration, index) => (
          <IntegrationItem
            key={`available-${integration?.integration?.featureCode}-${index}`}
            integration={integration}
            isActionPending={isActionPending}
            onActionClick={onActionClick}
            isExternalActionPending={props.isExternalActionPending}
            data-testid={props['data-testid']}
          />
        ))}
      </Show>
    </IntegrationPopper>
  );
}
